/**
 * SEO helpers for Vello Play.
 * Centralizes site-wide metadata, canonical URLs and JSON-LD builders.
 */

export const SITE_URL = "https://velloplay.com";
export const SITE_NAME = "Vello Play";
export const SITE_TAGLINE = "Plataforma de Gestão Esportiva para Torneios e Arenas";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export function buildCanonical(path: string): string {
  if (!path || path === "/") return `${SITE_URL}/`;
  // Strip query/hash and trailing slashes (except root)
  const clean = path.split("#")[0].split("?")[0].replace(/\/+$/, "");
  return `${SITE_URL}${clean.startsWith("/") ? clean : `/${clean}`}`;
}

export function pageTitle(title?: string): string {
  if (!title) return `${SITE_NAME} | ${SITE_TAGLINE}`;
  if (title.toLowerCase().includes("vello play")) return title;
  return `${title} | ${SITE_NAME}`;
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export function buildBreadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: buildCanonical(it.path),
    })),
  };
}

export function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/icon-512.png`,
    description: SITE_TAGLINE,
    sameAs: [],
  };
}

export function buildWebPageJsonLd(name: string, description: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: buildCanonical(path),
    inLanguage: "pt-BR",
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: `${SITE_URL}/`,
    },
  };
}

export function buildServiceJsonLd(name: string, description: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: buildCanonical(path),
    provider: { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` },
    areaServed: { "@type": "Country", name: "Brasil" },
  };
}

export function buildItemListJsonLd(name: string, items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    itemListElement: items.slice(0, 30).map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      url: it.url.startsWith("http") ? it.url : buildCanonical(it.url),
    })),
  };
}

export function buildSportsEventJsonLd(opts: {
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  image?: string;
  path: string;
  status?: "scheduled" | "cancelled" | "postponed" | "completed";
}) {
  const statusMap: Record<string, string> = {
    scheduled: "https://schema.org/EventScheduled",
    cancelled: "https://schema.org/EventCancelled",
    postponed: "https://schema.org/EventPostponed",
    completed: "https://schema.org/EventScheduled",
  };
  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: opts.name,
    description: opts.description,
    startDate: opts.startDate,
    endDate: opts.endDate,
    eventStatus: statusMap[opts.status ?? "scheduled"],
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: opts.image ? [opts.image] : undefined,
    url: buildCanonical(opts.path),
    location: opts.location
      ? { "@type": "Place", name: opts.location, address: opts.location }
      : undefined,
    organizer: { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` },
  };
}

export function buildSportsTeamJsonLd(opts: {
  name: string;
  sport?: string;
  image?: string;
  path: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SportsTeam",
    name: opts.name,
    sport: opts.sport,
    logo: opts.image,
    image: opts.image,
    description: opts.description,
    url: buildCanonical(opts.path),
  };
}

export function buildPersonJsonLd(opts: {
  name: string;
  image?: string;
  description?: string;
  path: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: opts.name,
    image: opts.image,
    description: opts.description,
    url: buildCanonical(opts.path),
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function buildFAQPageJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.answer,
      },
    })),
  };
}

export function buildSportsActivityLocationJsonLd(opts: {
  name: string;
  description?: string;
  image?: string;
  path: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  telephone?: string;
  sport?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    name: opts.name,
    description: opts.description,
    image: opts.image,
    url: buildCanonical(opts.path),
    telephone: opts.telephone,
    address: opts.address
      ? {
          "@type": "PostalAddress",
          ...opts.address,
          addressCountry: opts.address.addressCountry ?? "BR",
        }
      : undefined,
    sport: opts.sport,
  };
}

export function buildSoftwareApplicationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description: SITE_TAGLINE,
    url: `${SITE_URL}/`,
    inLanguage: "pt-BR",
    offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" },
  };
}
